"use client";
import { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons/faArrowRight";
import { getUrls } from "@portal/app/_common/lib/urls";
import { useMixpanel } from "@portal/app/_components/hooks/useMixpanel";
import { paletteOptions } from "@portal/app/_components/theme/options/palette";
import { useHasDashFooter } from "@portal/app/dash/_components/DashFooter";

interface StickyFindMyMatchCtaProps {
  zip: string;
}

const HERO_CTA_SELECTOR = '[data-testid="home-hero-find-my-match-cta"]';
const FOOTER_SELECTOR = '[data-testid="layout-footer"]';

export const StickyFindMyMatchCta = ({ zip }: StickyFindMyMatchCtaProps) => {
  const urls = getUrls();
  const analytics = useMixpanel();
  const hasDashFooter = useHasDashFooter();
  const [heroCtaVisible, setHeroCtaVisible] = useState(true);
  const [footerVisible, setFooterVisible] = useState(false);

  useEffect(() => {
    if (hasDashFooter) return;
    const heroCta = document.querySelector(HERO_CTA_SELECTOR);
    if (!heroCta) {
      setHeroCtaVisible(false);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setHeroCtaVisible(entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(heroCta);
    return () => observer.disconnect();
  }, [hasDashFooter]);

  useEffect(() => {
    if (hasDashFooter) return;
    const footerEl = document.querySelector(FOOTER_SELECTOR);
    if (!footerEl) return;
    const observer = new IntersectionObserver(
      ([entry]) => setFooterVisible(entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(footerEl);
    return () => observer.disconnect();
  }, [hasDashFooter]);

  // Logged-in users already have the DashFooter pinned to the bottom of the
  // viewport; rendering the sticky CTA on top of it would hide their primary
  // navigation on mobile.
  if (hasDashFooter) return null;

  const visible = !heroCtaVisible && !footerVisible;

  return (
    <StickyContainer $visible={visible} aria-hidden={!visible}>
      <StickyButton
        href={urls.FormFunnelHome()}
        variant="contained"
        color="primary"
        endIcon={<FontAwesomeIcon icon={faArrowRight} />}
        onClick={() =>
          analytics.track("Homepage - Clicked Find My Match", { zip, surface: "sticky" })
        }
        data-testid="home-sticky-find-my-match-cta"
        tabIndex={visible ? 0 : -1}
      >
        Find My Match
      </StickyButton>
    </StickyContainer>
  );
};

const StickyButton = styled(Button)`
  &.MuiButton-root {
    border-radius: 2.5rem;
    padding: 0.5rem 1.25rem;
    font-size: 0.9375rem;
    font-weight: 600;
    text-transform: none;
    line-height: 1.4;
    letter-spacing: 0.01em;
    min-width: 0;

    &:focus-visible {
      outline: 2px solid ${paletteOptions.primary.main};
      outline-offset: 2px;
    }

    .MuiButton-endIcon {
      margin-left: 0.5rem;

      svg {
        font-size: 0.8125rem;
      }
    }
  }
`;

const StickyContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "$visible",
})<{ $visible: boolean }>(
  ({ theme, $visible }) => `
  position: fixed;
  z-index: 1000;
  transition:
    opacity 240ms ease-out,
    visibility 240ms ease-out,
    transform 240ms ease-out,
    filter 200ms ease-out;
  opacity: ${$visible ? 1 : 0};
  visibility: ${$visible ? "visible" : "hidden"};
  pointer-events: ${$visible ? "auto" : "none"};
  transform: ${$visible ? "translateY(0)" : "translateY(8px)"};

  ${theme.breakpoints.up("md")} {
    bottom: 1.75rem;
    right: 1.75rem;
    filter:
      drop-shadow(0 6px 20px rgba(0, 0, 0, 0.18))
      drop-shadow(0 2px 4px rgba(0, 0, 0, 0.08));

    &:hover {
      transform: translateY(-2px);
      filter:
        drop-shadow(0 12px 28px rgba(0, 0, 0, 0.22))
        drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));
    }
  }

  ${theme.breakpoints.down("md")} {
    bottom: 0;
    left: 0;
    right: 0;
    padding: 0.625rem 1rem calc(0.625rem + env(safe-area-inset-bottom)) 1rem;
    background: white;
    border-top: 1px solid rgba(0, 0, 0, 0.08);

    .MuiButton-root {
      width: 100%;
    }
  }
`
);
